/**
 * A request, from anywhere in the app, to put the cursor in the chat
 * sidebar's message input. Each message input on screen when the request is
 * made takes the cursor at once. With none on screen the request is kept,
 * because the sidebar mounts its input only once the bar opens: the next
 * input to subscribe takes the cursor and clears it, unless
 * `dropChatInputFocusRequest` clears it first.
 */

const focusInputs = new Set<() => void>();

let focusRequested = false;

/** Asks the chat sidebar's message input to take the cursor as soon as it is
 *  on screen. */
export function requestChatInputFocus(): void {
  focusRequested = focusInputs.size === 0;
  focusInputs.forEach((focus) => focus());
}

/** Drops a request that no message input has answered yet. */
export function dropChatInputFocusRequest(): void {
  focusRequested = false;
}

/**
 * Calls `focus` for a request still waiting when this is called, and for each
 * request made while subscribed. Returns the function that unsubscribes.
 */
export function subscribeChatInputFocus(focus: () => void): () => void {
  if (focusRequested) {
    focusRequested = false;
    focus();
  }
  focusInputs.add(focus);
  return () => {
    focusInputs.delete(focus);
  };
}
